/**
 * Intersection Observer + requestAnimationFrame Parallax
 * Reference: https://coding-memo.work/javascript/1025/
 *
 * Usage:
 *   <div data-js="parallax">
 *     <div data-js="parallax-item">
 *       <img src="..." alt="">
 *     </div>
 *   </div>
 */

export default class Parallax {
  constructor() {
    const targets = document.querySelectorAll('[data-js="parallax"]');
    if (targets.length === 0) return;

    let winHeight = window.innerHeight;
    const instances = [];

    let resizeRaf;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        winHeight = window.innerHeight;
        instances.forEach(inst => inst.recalc());
      });
    });

    targets.forEach(target => {
      const child = target.querySelector('[data-js="parallax-item"]');
      if (!child) return;

      let isVisible = false;
      let frameId = 0;
      let lastPosition = null;
      let offsetTop, scrollRatio;

      const getPositionRatio = () => {
        offsetTop = target.getBoundingClientRect().top + window.pageYOffset;
        const targetHeight = target.clientHeight;
        const childHeight = child.clientHeight;
        // childがtargetより短い場合は0にクランプ（逆方向移動を防止）
        scrollRatio = childHeight > targetHeight
          ? (childHeight - targetHeight) / (winHeight + targetHeight)
          : 0;
      };

      const recalc = () => {
        // transformリセットして正確な寸法を取得
        child.style.transform = '';
        getPositionRatio();

        // 可視状態なら即座に正しいtransformを再適用
        if (isVisible && scrollRatio > 0) {
          const scrollTop = window.pageYOffset;
          const scrollBottom = scrollTop + winHeight;
          const setVal = ((scrollBottom - offsetTop) * scrollRatio).toFixed(1);
          child.style.transform = 'translate3d(0,' + (setVal * -1) + 'px,0)';
        }
        lastPosition = null;
      };
      recalc();

      instances.push({ recalc });

      const resizeObserver = new ResizeObserver(recalc);
      resizeObserver.observe(target);

      const observerFunc = entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            getPositionRatio();
            isVisible = true;
            child.style.willChange = 'transform';
            frameId = requestAnimationFrame(parallaxFunc);
          } else {
            isVisible = false;
            cancelAnimationFrame(frameId);
            child.style.willChange = '';
          }
        });
      };

      const observer = new IntersectionObserver(observerFunc);
      observer.observe(target);

      const framesPerSecond = 60;
      const interval = Math.floor(1000 / framesPerSecond);
      let previousTime = performance.now();
      let currentTime = 0;
      let elapsed = 0;

      const parallaxFunc = timestamp => {
        const scrollTop = Number(window.pageYOffset.toFixed(0));

        if (lastPosition === scrollTop) {
          frameId = requestAnimationFrame(parallaxFunc);
          return;
        }

        currentTime = timestamp;
        elapsed = currentTime - previousTime;
        if (elapsed <= interval) {
          frameId = requestAnimationFrame(parallaxFunc);
          return;
        }
        previousTime = currentTime - (elapsed % interval);

        const scrollBottom = scrollTop + winHeight;
        const setVal = ((scrollBottom - offsetTop) * scrollRatio).toFixed(1);

        child.style.transform = 'translate3d(0,' + (setVal * -1) + 'px,0)';

        if (isVisible) {
          frameId = requestAnimationFrame(parallaxFunc);
        }

        lastPosition = scrollTop;
      };
    });
  }
}
